import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Modal, Button, Form } from "react-bootstrap";

interface CropperModalProps {
  show: boolean;
  onHide: () => void;
  imageSrc: string;
  onCropComplete: (croppedImageBlob: Blob, croppedImageUrl: string) => void;
  aspectRatio?: number;
  title?: string;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: CropArea
): Promise<{ blob: Blob; url: string }> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve({
            blob,
            url: URL.createObjectURL(blob),
          });
        }
      },
      "image/jpeg",
      0.9
    );
  });
};

function ImageCropperModal({
  show,
  onHide,
  imageSrc,
  onCropComplete,
  aspectRatio = 4 / 3,
  title = "Crop Image",
}: CropperModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
    null
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteInternal = useCallback(
    (croppedArea: CropArea, croppedAreaPixels: CropArea) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleSaveCrop = async () => {
    if (!croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      const { blob, url } = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(blob, url);
      onHide();
    } catch (error) {
      console.error("Error cropping image:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} size="lg" backdrop="static">
      <Modal.Header className="border" closeButton>
        <Modal.Title>
          <i className="fas fa-crop me-2"></i>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="border">
        <div
          className="bg-dark"
          style={{
            position: "relative",
            width: "100%",
            height: "400px",
            overflow: "hidden",
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={onCropChange}
            onCropComplete={onCropCompleteInternal}
            onZoomChange={onZoomChange}
            style={{
              containerStyle: {
                borderRadius: "8px",
              },
            }}
          />
        </div>

        <div className="mt-3">
          <Form.Label className="fw-bold">
            <i className="fas fa-search-plus me-2"></i>
            Zoom: {zoom.toFixed(1)}x
          </Form.Label>
          <Form.Range
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>

        <h6 className="mt-2 small">
          <i className="fas fa-info-circle me-1"></i>
          Drag to reposition • Scroll or use slider to zoom • Aspect ratio:{" "}
          {aspectRatio === 1 ? "1:1 (Square)" : `${aspectRatio.toFixed(2)}:1`}
        </h6>
      </Modal.Body>

      <Modal.Footer className="shadow border">
        <button
          className="btn btn-secondary btn-flat"
          onClick={handleCancel}
          disabled={isProcessing}
        >
          <i className="fas fa-times me-2"></i>
          Cancel
        </button>
        <button
          className="btn btn-primary btn-flat"
          onClick={handleSaveCrop}
          disabled={!croppedAreaPixels || isProcessing}
        >
          {isProcessing ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Processing...
            </>
          ) : (
            <>
              <i className="fas fa-check me-2"></i>
              Apply Crop
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default ImageCropperModal;
